import * as XLSX from 'xlsx';
// Bundle from source so dependency overrides also patch the browser's XML parser.
import * as mammoth from 'mammoth';
import {
  CARE_SOURCE_DOCUMENT_TYPES,
  CARE_SOURCE_TEXT_EXTRACTION_STATUSES,
} from '../careSourceMetadata';
import { readCareSourceImageDimensions } from './careSourceImageDimensions';

export const MAX_CARE_SOURCE_FILE_SIZE_BYTES = 20 * 1024 * 1024;
export const MAX_CARE_SOURCE_IMAGE_DIMENSION_PIXELS = 12000;
export const MAX_CARE_SOURCE_IMAGE_TOTAL_PIXELS = 40 * 1000 * 1000;
export const MAX_CARE_SOURCE_PDF_PAGES = 200;

export const validateCareSourceFileSize = (file) => {
  if (!file) throw new Error('No file selected.');
  if (file.size > MAX_CARE_SOURCE_FILE_SIZE_BYTES) {
    throw new Error('That file is larger than the 20 MB upload limit.');
  }
};

const getFileExtension = (fileName = '') => {
  const parts = String(fileName).toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() : '';
};

const readFileAsArrayBuffer = async (file) => {
  if (typeof file?.arrayBuffer === 'function') {
    return file.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Unable to read file.'));
    reader.onload = () => resolve(reader.result);
    reader.readAsArrayBuffer(file);
  });
};

const readFileAsText = async (file) => {
  if (typeof file?.text === 'function') {
    return file.text();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Unable to read file.'));
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsText(file);
  });
};

const getNormalizedMimeType = (file = {}) => String(file?.type || '').trim().toLowerCase();

const isPlainTextFile = (extension = '', mimeType = '') => (
  extension === 'txt'
  || extension === 'csv'
  || extension === 'tsv'
  || mimeType === 'text/plain'
  || mimeType === 'text/csv'
  || mimeType === 'text/tab-separated-values'
);

const isPdfFile = (extension = '', mimeType = '') => (
  extension === 'pdf'
  || mimeType === 'application/pdf'
);

export const isImageFile = (extension = '', mimeType = '') => (
  ['png', 'jpg', 'jpeg', 'heic', 'webp'].includes(extension)
  || mimeType.startsWith('image/')
);

const normalizePdfTextItems = (items = []) => {
  const segments = [];
  let previousY = null;

  items.forEach((item) => {
    const text = String(item?.str || '').trim();
    if (!text) return;

    const y = Array.isArray(item?.transform) ? Math.round(Number(item.transform[5]) || 0) : null;
    const shouldBreakLine = item?.hasEOL === true
      || (previousY !== null && y !== null && Math.abs(previousY - y) > 2);

    if (segments.length === 0 || shouldBreakLine) {
      segments.push(text);
    } else {
      segments[segments.length - 1] = `${segments[segments.length - 1]} ${text}`.trim();
    }

    previousY = y;
  });

  return segments.join('\n');
};

export const extractTextFromPdfArrayBuffer = async (arrayBuffer) => {
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
  const globalScope = typeof window !== 'undefined' ? window : {};

  if (!globalScope.pdfjsWorker?.WorkerMessageHandler) {
    globalScope.pdfjsWorker = await import('pdfjs-dist/legacy/build/pdf.worker.mjs');
  }

  const loadingTask = pdfjs.getDocument({
    data: arrayBuffer,
    useWorkerFetch: false,
    isEvalSupported: false,
    disableFontFace: true,
  });

  const pdf = await loadingTask.promise;
  const pageTexts = [];

  try {
    if (pdf.numPages > MAX_CARE_SOURCE_PDF_PAGES) {
      throw new Error('This PDF has too many pages to organize automatically.');
    }
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const textContent = await page.getTextContent();
      const pageText = normalizePdfTextItems(textContent?.items || []).trim();
      if (pageText) {
        pageTexts.push(pageText);
      }
      page.cleanup();
    }
  } finally {
    if (typeof loadingTask?.destroy === 'function') {
      await loadingTask.destroy();
    }
  }

  return pageTexts.join('\n\n').trim();
};

const extractTextFromSpreadsheet = (arrayBuffer) => {
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const includeSheetHeaders = workbook.SheetNames.length > 1;
  const extractedText = workbook.SheetNames.map((sheetName) => {
    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });
    const normalizedRows = rows
      .map((row) => (Array.isArray(row) ? row.map((cell) => String(cell).trim()).filter(Boolean).join('\t') : String(row).trim()))
      .filter(Boolean);

    return (includeSheetHeaders ? [`Sheet: ${sheetName}`, ...normalizedRows] : normalizedRows).join('\n');
  }).join('\n\n');

  return extractedText.trim();
};

export const extractTextFromCareSourceFile = async (file) => {
  validateCareSourceFileSize(file);

  const extension = getFileExtension(file.name);
  const mimeType = getNormalizedMimeType(file);

  if (isPlainTextFile(extension, mimeType)) {
    return String(await readFileAsText(file)).trim();
  }

  const arrayBuffer = await readFileAsArrayBuffer(file);

  if (extension === 'xls' || extension === 'xlsx') {
    return extractTextFromSpreadsheet(arrayBuffer);
  }

  if (extension === 'docx') {
    const result = await mammoth.extractRawText({ arrayBuffer });
    return (result.value || '').trim();
  }

  if (isPdfFile(extension, mimeType)) {
    return extractTextFromPdfArrayBuffer(arrayBuffer);
  }

  throw new Error('Unsupported file type. Please upload a .pdf, .txt, .csv, .tsv, .xls, .xlsx, or .docx file.');
};

export const prepareCareSourceFileForUpload = async (file) => {
  validateCareSourceFileSize(file);

  const extension = getFileExtension(file.name);
  const mimeType = getNormalizedMimeType(file);

  if (isImageFile(extension, mimeType)) {
    const dimensions = await readCareSourceImageDimensions(file);
    const width = Number(dimensions?.width || 0);
    const height = Number(dimensions?.height || 0);
    if (!width || !height) throw new Error('The image dimensions could not be verified.');
    if (width > MAX_CARE_SOURCE_IMAGE_DIMENSION_PIXELS
      || height > MAX_CARE_SOURCE_IMAGE_DIMENSION_PIXELS
      || width * height > MAX_CARE_SOURCE_IMAGE_TOTAL_PIXELS) {
      throw new Error('Image dimensions are too large.');
    }
    return {
      rawText: '',
      textExtractionStatus: CARE_SOURCE_TEXT_EXTRACTION_STATUSES.PENDING,
      documentType: CARE_SOURCE_DOCUMENT_TYPES.IMAGE,
      extractionError: {
        code: 'ocr-pending',
        message: 'Image text will be read after the source is saved.',
      },
      userMessage: 'Image saved. Text extraction will be added next.',
    };
  }

  try {
    const rawText = await extractTextFromCareSourceFile(file);
    if (!rawText) {
      return {
        rawText: '',
        textExtractionStatus: CARE_SOURCE_TEXT_EXTRACTION_STATUSES.FAILED,
        extractionError: {
          code: 'empty-extraction',
          message: 'We could not read text from this file yet.',
        },
        userMessage: 'Source saved, but we could not read text from it yet.',
      };
    }

    return {
      rawText,
      textExtractionStatus: CARE_SOURCE_TEXT_EXTRACTION_STATUSES.EXTRACTED,
      extractionError: null,
      userMessage: 'Source saved.',
    };
  } catch (error) {
    const pageLimitReached = error?.message === 'This PDF has too many pages to organize automatically.';
    return {
      rawText: '',
      textExtractionStatus: CARE_SOURCE_TEXT_EXTRACTION_STATUSES.FAILED,
      extractionError: {
        code: pageLimitReached ? 'pdf-page-limit' : 'text-extraction-failed',
        message: error?.message || 'We could not read text from this file yet.',
      },
      userMessage: pageLimitReached
        ? 'Source saved, but this PDF has too many pages to organize automatically.'
        : 'Source saved, but we could not read text from it yet.',
    };
  }
};
